import { Injectable } from '@angular/core';
import { CallResults, Categories, DropReasons, FlightDirections, LogTypes, MarketingDetailReferral, NotePriorities, NoteReminderTypes, TripDesignation } from 'src/app/api/commonInterfaces';
import { PotentialTripService } from 'src/app/api/potential-trip.service';
import { ContactRoles, ContactSubscription, PhoneTypes } from 'src/app/api/types/contacts';
import { ChargeTypes, PaymentTypes } from 'src/app/api/types/finance';
import { ItineraryColor } from 'src/app/api/types/itinerary';
import { DropdownOption, YesNoOption } from '../uiInterfaces';
import { TripDesignationService } from 'src/app/api/trip-designation.service';
import { Observable, of, switchMap } from 'rxjs';
import { DateTime } from 'luxon';
import { ProfileLocation } from 'src/app/api/linkable-locations.service';
import { BackgroundCheckTypes, ConsultingReportOptions, CustomerDonationSourceType, CustomerGroupType, Disaster, FundraisingStatus, PartnerLogType, PotentialGiftFrequency, PotentialGiftStatus } from 'src/app/api';
import { FormQuestionTypes } from 'src/app/api/types/forms';
import { TripAccommodations, TripDateRuleDayOfWeek } from 'src/app/api/types/tripDates';
import { potentialGiftFrequencyLabels, potentialGiftStatusLabels } from 'src/app/api/potential-gifts.service';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from 'src/app/config.service';


export interface Options<T> {
  label: string,
  value: T,
  disabled?: boolean
}

export interface Denominations {
  id: number,
  name: string,
  active: number,
  label?: string,
  value?: string
}

export type NoteTypeValue = "" | `${LogTypes.system}` | `${LogTypes.message}` | `${LogTypes.toCustomer}` | `${LogTypes.call}-${CallResults}` | `${LogTypes.emailOrText}` | `${LogTypes.meeting}` | `${LogTypes.task}` | `${LogTypes.note}`

export interface NoteCategoryValue { category?: Categories }

export interface PartnerLogCategoryValue { logtype?: PartnerLogType }

export type TripType = "ppm" | "special" | "leaderMission" | "leaderSponsor" | "leaderTrip" | "disaster" | "fmsc" | "yc"

@Injectable({
  providedIn: 'root'
})
export class DropdownOptionService {

  constructor(
    private pTripService: PotentialTripService,
    private tripDesignationService: TripDesignationService,
    private config: ConfigService,
    private http: HttpClient
  ) { }

  denominations(): Observable<DropdownOption[]> {
    return this.http.get<Denominations[]>(`${this.config.dataApiUrl}/mc-type-denominations`).pipe(
      switchMap(data => {
        const options: DropdownOption[] = [
          { value: "", label: "Not Specified" },
          ...data.filter(d => d.active === 1).map(d => ({
            value: d.name,
            label: d.name
          }))
        ];
        return of(options);
      })
    );
  }

  recruitingStatus() {
    return [
      { value: "-1", label: "Not Interested" },
      { value: "0", label: "Unqualified (0%)" },
      { value: "5", label: "Qualified (5%)" },
      { value: "15", label: "Does Missions (15%)" },
      { value: "30", label: "Interested in PPM (30%)" }
    ]
  }

  fundraisingStatuses(): Options<FundraisingStatus>[] {
    return [
      { value: FundraisingStatus.Not_Set, label: "Not Set" },
      { value: FundraisingStatus.Prospect, label: "Prospect" },
      { value: FundraisingStatus.New_Donor, label: "New Donor" },
      { value: FundraisingStatus.Active_Donor, label: "Active Donor" },
      { value: FundraisingStatus.Lapsed_Donor, label: "Lapsed Donor" },
      { value: FundraisingStatus.Recurring_Donor, label: "Recurring Donor" }
    ]
  }

  getRoleOptions() {
    return [
      { label: 'Group Leaders', value: 'gl' },
      { label: 'Participants', value: 'participant' },
      { label: 'All', value: 'all' }
    ]
  }

  renewalOptions() {
    return [
      { label: 'All Teams', value: 'all' },
      { label: 'New Teams', value: 'new' },
      { label: 'Renewal Teams', value: 'renewal' }
    ]
  }

  closedOptions() {
    return [
      { label: 'Hide Closed Trips', value: 0 },
      { label: 'Hide Open Trips', value: 1 },
      { label: 'Show All Trips', value: -1 }
    ]
  }

  yesNo(options?: { allowNotSet: boolean }): YesNoOption[] {

    let returnArray: YesNoOption[] = []

    if (options?.allowNotSet) {
      returnArray.push({ label: "Not Set", value: null })
    }

    returnArray.push({ label: "Yes", value: "1" })
    returnArray.push({ label: "No", value: "0" })

    return returnArray
  }

  noYes(): YesNoOption[] {
    return [
      { label: "No", value: "1" },
      { label: "Yes", value: "0" }
    ]
  }

  mailingList(): YesNoOption[] {
    return [
      { label: "Subscribed", value: "1" },
      { label: "Unsubscribed", value: "0" }
    ]
  }

  partnerTypes(): DropdownOption[] {
    return [
      { value: 'Church/Ministry', label: 'Church/Ministry' },
      { value: 'School', label: 'School' },
      { value: 'Mercy', label: 'Mercy/Nonprofit' },
      { value: 'Clinic', label: 'Clinic/Hospital' },
      { value: 'Hotel/Lodging', label: 'Hotel/Lodging' },
      { value: 'Restaurant', label: 'Restaurant' },
      { value: 'Transportation', label: 'Transportation' },
      { value: 'Recreation', label: 'Recreation' },
      { value: 'Retail', label: 'Retail' },
    ];
  }

  newCustomerSize(): DropdownOption[] {
    return [
      { value: "0", label: "Group" },
      { value: "1", label: "Individual" },
    ]
  }


  noteTypes(): Options<NoteTypeValue>[] {
    return [
      { label: "", value: "" },
      { label: "Left Voicemail", value: `${LogTypes.call}-${CallResults.leftVoicemail}` },
      { label: "Successful Call", value: `${LogTypes.call}-${CallResults.successfulCall}` },
      { label: "Attempted Call", value: `${LogTypes.call}-${CallResults.attemptedCall}` },
      { label: "Email/Text", value: `${LogTypes.emailOrText}` },
      { label: "In Person Meeting", value: `${LogTypes.meeting}` },
      { label: "Task", value: `${LogTypes.task}` },
      { label: "Note", value: `${LogTypes.note}` }
    ]
  }

  parseNoteTypeValue(value: NoteTypeValue): { logType: LogTypes, callResult: CallResults | undefined } {
    const activitySplit = value.split("-")

    let logType: LogTypes = LogTypes.note
    let callResult: CallResults | undefined = undefined

    if (activitySplit[0]) {
      logType = Number(activitySplit[0]) as LogTypes
    }
    if (activitySplit[1]) {
      callResult = Number(activitySplit[1]) as unknown as CallResults  | undefined
    }
    return { logType, callResult }
  }

  convertToNoteTypeValue(logType: LogTypes, callResult: CallResults | undefined): NoteTypeValue {
    
      if(logType === LogTypes.call) {
        return `${logType}-${callResult ?? CallResults.none}`
      }
      else {
        return `${logType}`
      }

   
  }

  CallTypes(): DropdownOption[] {
    return [
      { value: 0, label: 'All Call Types' },
      { value: 1, label: 'Consulting Calls' },
      { value: 2, label: 'Recruiting Calls' }
    ]
  }

  hasFutureTripOptions(): Options<boolean | null>[] {
    return [
      { value: null, label: 'All' },
      { value: true, label: 'Registered' },
      { value: false, label: 'Not Registered' },
    ]
  }

  scheduledActivityTypes(): Options<NoteTypeValue>[] {
    return [
      { label: "", value: "" },
      { label: "Call", value: `${LogTypes.call}-${CallResults.none}` },
      { label: "Email/Text", value: `${LogTypes.emailOrText}` },
      { label: "In Person Meeting", value: `${LogTypes.meeting}` },
      { label: "Task", value: `${LogTypes.task}` },
      { label: "Note", value: `${LogTypes.note}` }
    ]
  }

  noteCategories(args?: {includeSystem?: boolean}): Options<NoteCategoryValue>[] {
    
    const includeSystem = args?.includeSystem ?? false;

    const categories = [
      { label: "", value: {} },
      { label: "Consulting", value: { category: Categories.consulting } },
      { label: "Recruiting", value: { category: Categories.recruiting } },
      { label: "Finance", value: { category: Categories.finance } },
      { label: "Travel", value: { category: Categories.travel } },
      { label: "Fundraising", value: { category: Categories.fundraising } },
      { label: "Donor Engagement", value: { category: Categories.donorEngagement } },
      { label: "Other", value: { category: Categories.other } }

    ]

    if (includeSystem) {
      categories.push({ label: "System", value: { category: Categories.none } })
    }

    return categories

  }

  partnerLogCategories(): Options<PartnerLogCategoryValue>[] {
    return [
      { label: "", value: {} },
      { label: "Note", value: { logtype: PartnerLogType.note } },
      { label: "Task", value: { logtype: PartnerLogType.task } },
      { label: "Meeting", value: { logtype: PartnerLogType.meeting } },
      { label: "Email/Text", value: { logtype: PartnerLogType.email } },
      { label: "Call", value: { logtype: PartnerLogType.call } }
    ]
  }

  notePriorities(): Options<NotePriorities>[] {
    return [
      { label: "Low Priority", value: NotePriorities.Low },
      { label: "Normal Priority", value: NotePriorities.Normal },
      { label: "High Priority", value: NotePriorities.High }
    ]
  }

  noteReminderTypes(): Options<NoteReminderTypes>[] {
    return [
      { label: "Email", value: NoteReminderTypes.Email },
      { label: "Slack", value: NoteReminderTypes.Slack }
    ]
  }

  phoneTypeOptions(): Options<PhoneTypes>[] {
    return [
      { label: "-", value: PhoneTypes.NotSet },
      { label: "Mobile", value: PhoneTypes.Mobile },
      { label: "Home", value: PhoneTypes.Home },
      { label: "Office", value: PhoneTypes.Office },
      { label: "Other", value: PhoneTypes.Other }
    ]
  }

  contactRoles(): Options<ContactRoles>[] {
    return [
      { label: "Primary Contact", value: "Primary Contact" },
      { label: "Alternate Contact", value: "Alternate Contact" },
      { label: "Past Contact", value: "Past contact" },
      { label: "Consultant", value: "Consultant" },
      { label: "Decision Maker", value: "Decision Maker" },
      { label: "Financial", value: "Financial contact" },
      { label: "Senior Leadership", value: "Senior Leadership" },
      { label: "Other", value: "Other" },
      { label: "Potential Donor", value: "Potential Donor" },
      { label: "Active Donor", value: "Active Donor" },
      { label: "Inactive Donor", value: "Inactive Donor" }
    ]
  }

  contactSubscriptionStatuses(): Options<ContactSubscription>[] {
    return [
      { label: "Soft Opt-in", value: "Soft Opt-In" },
      { label: "Confirmed Opt-in", value: "Confirmed Opt-In" },
      { label: "Soft Opt-out", value: "Soft Opt-Out" },
      { label: "Confirmed Opt-out", value: "Confirmed Opt-Out" }
    ]
  }

  potentialTripStatuses(): Options<number>[] {

    return [
      { label: `30% - ${this.pTripService.getStatusString(30)}`, value: 30 },
      { label: `50% - ${this.pTripService.getStatusString(50)}`, value: 50 },
      { label: `75% - ${this.pTripService.getStatusString(75)}`, value: 75 },
      { label: `100% - ${this.pTripService.getStatusString(100)}`, value: 100 },
      { label: `0% - ${this.pTripService.getStatusString(0)}`, value: 0 },
    ]
  }

  potentialTripHurdles(): Options<string>[] {
    return [
      { label: 'Recruiting participants', value: "Recruiting Participants" },
      { label: 'Safety', value: "Safety" },
      { label: 'Cost', value: "Cost" },
      { label: "Timing", value: "Timing" },
      { label: "Other Org", value: "Other Org" }
    ]
  }

  tripTypes(): Options<TripType>[] {
    return [
      { label: "Standard", value: "ppm" },
      { label: "FMSC", value: "fmsc" },
      { label: "Vision Trip", value: "special" },
      { label: "Leader Mission", value: "leaderMission" },
      { label: "Leader Sponsor", value: "leaderSponsor" },
      { label: "Leader Trip", value: "leaderTrip" },
      { label: "Disaster", value: "disaster" },
      { label: "Youth Cartel", value: "yc" }
    ]
  }

  currentPastTeam(): Options<number>[] {
    return [
      { label: "Include All", value: 0 },
      { label: "Current Teams", value: 1 },
      { label: "Past Teams", value: 2 },
      { label: "Current and Past Teams", value: 3 },
      { label: "Never Taken a Trip", value: 4 }
    ]
  }

  addChargeTypes(): Options<ChargeTypes>[] {
    return [
      { label: "Discount", value: ChargeTypes.Discount },
      { label: "Additional Charges", value: ChargeTypes.Additional_Charges },
      { label: "Ministry Money", value: ChargeTypes.Ministry_Money }
    ]
  }

  editChargeTypes(): Options<ChargeTypes>[] {
    return [
      { label: "Discount", value: ChargeTypes.Discount },
      { label: "Additional Charges", value: ChargeTypes.Additional_Charges },
      { label: "Ministry Money", value: ChargeTypes.Ministry_Money },
      { label: "Deposit/Registration Fee", value: ChargeTypes.Registration },
      { label: "First payment", value: ChargeTypes.First_Payment },
      { label: "Final Payment", value: ChargeTypes.Final_Payment }
    ]
  }

  itineraryEventColors(): Options<ItineraryColor>[] {
    return [
      { label: "Red", value: "red" },
      { label: "Pink", value: "pink" },
      { label: "Purple", value: "purple" },
      { label: "Violet", value: "violet" },
      { label: "Indigo", value: "indigo" },
      { label: "Blue", value: "blue" },
      { label: "Cyan", value: "cyan" },
      { label: "Green", value: "green" },
      { label: "Lime", value: "lime" },
      { label: "Yellow", value: "yellow" }
    ]
  }

  dropReasons(): Options<DropReasons>[] {
    return [
      { label: DropReasons.Finances, value: DropReasons.Finances },
      { label: DropReasons.GL_Change, value: DropReasons.GL_Change },
      { label: DropReasons.Location, value: DropReasons.Location },
      { label: DropReasons.Timing, value: DropReasons.Timing },
      { label: DropReasons.Not_Ready, value: DropReasons.Not_Ready },
      { label: DropReasons.No_Communication, value: DropReasons.No_Communication },
      { label: DropReasons.Another_Org, value: DropReasons.Another_Org },
      { label: DropReasons.Recruiting, value: DropReasons.Recruiting },
      { label: DropReasons.Advisory, value: DropReasons.Advisory },
      { label: DropReasons.No_GL_Communication, value: DropReasons.No_GL_Communication },
      { label: DropReasons.Emergency, value: DropReasons.Emergency },

    ]
  }

  tripDesignations(): Observable<Options<string>[]> {
    return this.tripDesignationService.getDesignations().pipe(
      switchMap(designations => of(designations.map(d => { return { label: d.name, value: d.id.toString() } })))
    )
  }

  genderOptions(): Options<string>[] {
    return [
      { label: 'Not specified', value: '0' },
      { label: 'Male', value: '1' },
      { label: 'Female', value: '2' },
    ]
  }

  paymentTypes(): Options<PaymentTypes>[] {
    return [
      { label: "Check", value: PaymentTypes.Check },
      { label: "Stripe", value: PaymentTypes.Stripe },
      { label: "PayPal", value: PaymentTypes.PayPal },
      { label: "Transfer", value: PaymentTypes.Transfer },
      { label: "Transfer to Wallet", value: PaymentTypes.TransferToWallet },
      { label: "Refund", value: PaymentTypes.Refund },
      { label: "PPM Global", value: PaymentTypes.PpmGlobal },
      { label: "ACH", value: PaymentTypes.ACH },
      { label: "Other", value: PaymentTypes.Other }
    ]
  }

  flightDirections(): Options<FlightDirections>[] {
    return [
      { label: "Going to Destination", value: FlightDirections.To },
      { label: "Coming Back Home", value: FlightDirections.From }
    ]
  }

  allTripYears(args?: { startYear?: number, futureYearCount?: number }): Options<number>[] {

    const localArgs = {
      startYear: 2007,
      futureYearCount: 0,
      ...args
    }



    let year = localArgs.startYear
    let options: Options<number>[] = []
    while (year <= new Date().getFullYear() + localArgs.futureYearCount) {
      options.push({ label: year.toString(), value: year })
      year += 1
    }

    return options
  }

  marketingDetailReferrals(): Options<MarketingDetailReferral>[] {
    return [
      { label: "Not Answered", value: MarketingDetailReferral.Empty },
      { label: "Global Leadership Summit", value: MarketingDetailReferral.GlobalLeadershipSummit },
      { label: "Youth Pastor", value: MarketingDetailReferral.YouthPastor },
      { label: "Google Search", value: MarketingDetailReferral.GoogleSearch },
      { label: "Other Organization", value: MarketingDetailReferral.OtherOrg },
      { label: "Marko", value: MarketingDetailReferral.Marko }
    ]
  }


  months(type: "short" | "long" = "short"): Options<number>[] {
    switch (type) {
      case "short": {
        return [
          { label: "Jan", value: 0 },
          { label: "Feb", value: 1 },
          { label: "Mar", value: 2 },
          { label: "Apr", value: 3 },
          { label: "May", value: 4 },
          { label: "Jun", value: 5 },
          { label: "Jul", value: 6 },
          { label: "Aug", value: 7 },
          { label: "Sep", value: 8 },
          { label: "Oct", value: 9 },
          { label: "Nov", value: 10 },
          { label: "Dec", value: 11 },
        ]
      }
      case "long": {
        return [
          { label: "January", value: 0 },
          { label: "February", value: 1 },
          { label: "March", value: 2 },
          { label: "April", value: 3 },
          { label: "May", value: 4 },
          { label: "June", value: 5 },
          { label: "July", value: 6 },
          { label: "August", value: 7 },
          { label: "September", value: 8 },
          { label: "October", value: 9 },
          { label: "November", value: 10 },
          { label: "December", value: 11 },
        ]
      }
    }
  }

  partnerLogTypes(): Options<PartnerLogType>[] {
    return [
      { label: "Note", value: PartnerLogType.note },
      { label: "Task", value: PartnerLogType.task },
      { label: "In a Person Meeting", value: PartnerLogType.meeting },
      { label: "Email/Text", value: PartnerLogType.email },
      { label: "Call", value: PartnerLogType.call }
    ]
  }

  daysForMonth(month: number, year?: number | null | "") {

    let baseDays = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28]

    switch (month) {

      case 3:
      case 5:
      case 8:
      case 10:
        return [...baseDays, 29, 30]
      case 1:
        if (year) {
          const finalDay = DateTime.fromJSDate(new Date(year, 1, 1)).endOf("month").day
          if (finalDay === 28) return [...baseDays]
          else return [...baseDays, 29]
        }
        else {
          return [...baseDays, 29]
        }
      case 0:
      case 2:
      case 4:
      case 6:
      case 7:
      case 9:
      case 11:
      default:
        return [...baseDays, 29, 30, 31]

        throw new Error("Invalid month provided")
    }
  }

  locations(locations: ProfileLocation[]): Options<string>[] {
    return locations.map(l => {
      return { label: l.name, value: l.id }
    })
  }

  disasters(disasters: Disaster[]): Options<number>[] {
    return disasters.map(d => {
      return { label: d.title, value: d.id }
    })
  }

  tripBackgroundCheckTypes(): Options<string>[] {
    return [
      { label: "Church/Organization Managed", value: BackgroundCheckTypes.SelfManaged.toString() },
      { label: "Sterling", value: BackgroundCheckTypes.Sterling.toString() },
      { label: "Waived", value: BackgroundCheckTypes.Waived.toString(), disabled: true },
      { label: "Mixed", value: BackgroundCheckTypes.Mixed.toString() }
    ]
  }

  backgroundCheckTypes(): Options<BackgroundCheckTypes>[] {
    return [
      { label: "Church/Organization Managed", value: BackgroundCheckTypes.SelfManaged },
      { label: "Sterling", value: BackgroundCheckTypes.Sterling },
      { label: "Waived", value: BackgroundCheckTypes.Waived }
    ]
  }

  formQuestionTypes(): Options<FormQuestionTypes>[] {
    return [
      { label: "Text Response", value: FormQuestionTypes.Text },
      { label: "Number Response", value: FormQuestionTypes.Numeric },
      { label: "Scale (out of 5)", value: FormQuestionTypes.NumericScale },
      { label: "ATL Evaluation", value: FormQuestionTypes.AtlEval },
      { label: "TL Evaluation", value: FormQuestionTypes.TlEval },
      { label: "Selection List", value: FormQuestionTypes.SingleOptionSelect },
      { label: "Multi-Select List", value: FormQuestionTypes.MultiOptionSelect }
    ]
  }

  tripRoles(): Options<"tl" | "atl" | "">[] {
    return [
      { label: "Any", value: "" },
      { label: "Trip Leader", value: "tl" },
      { label: "Assistant Trip Leader", value: "atl" }
    ]
  }

  tripDateRuleDayofWeek(): Options<TripDateRuleDayOfWeek>[] {
    return [
      { label: "Sunday", value: TripDateRuleDayOfWeek.Sunday },
      { label: "Monday", value: TripDateRuleDayOfWeek.Monday },
      { label: "Tuesday", value: TripDateRuleDayOfWeek.Tuesday },
      { label: "Wednesday", value: TripDateRuleDayOfWeek.Wednesday },
      { label: "Thursday", value: TripDateRuleDayOfWeek.Thursday },
      { label: "Friday", value: TripDateRuleDayOfWeek.Friday },
      { label: "Saturday", value: TripDateRuleDayOfWeek.Saturday }
    ]
  }

  tripAccommodations(): Options<string>[] {
    return [
      { label: "Not specified", value: TripAccommodations.NotSpecified },
      { label: "Bunk", value: TripAccommodations.Bunk },
      { label: "Floor", value: TripAccommodations.Floor },
      { label: "Hotel", value: TripAccommodations.Hotel },
      { label: "Other", value: TripAccommodations.Other },
    ]
  }

  tripSearchDisplayOptions(): Options<number>[] {
    return [
      { label: "Show as limited availability", value: 0 },
      { label: "Remove from trip list", value: 1 }
    ]
  }

  consultingReportOptions(): Options<ConsultingReportOptions>[] {
    return [
      { label: "Hide Teams w/ Contact", value: "hide-contact" },
      { label: "Hide Teams w/ Paid Registration Fee", value: "hide-paid-fee" },
      { label: "Have Paid First Payment", value: "no-first-payment" },
      { label: "Have Not Paid First Payment", value: "yes-first-payment" }
    ]
  }

  states(): Options<string>[] {
    return [
      {label: "Alabama", value: "AL"},
      {label: "Alaska", value: "AK"},
      {label: "Arizona", value: "AZ"},
      {label: "Arkansas", value: "AR"},
      {label: "California", value: "CA"},
      {label: "Colorado", value: "CO"},
      {label: "Connecticut", value: "CT"},
      {label: "Delaware", value: "DE"},
      {label: "Florida", value: "FL"},
      {label: "Georgia", value: "GA"},
      {label: "Hawaii", value: "HI"},
      {label: "Idaho", value: "ID"},
      {label: "Illinois", value: "IL"},
      {label: "Indiana", value: "IN"},
      {label: "Iowa", value: "IA"},
      {label: "Kansas", value: "KS"},
      {label: "Kentucky", value: "KY"},
      {label: "Louisiana", value: "LA"},
      {label: "Maine", value: "ME"},
      {label: "Maryland", value: "MD"},
      {label: "Massachusetts", value: "MA"},
      {label: "Michigan", value: "MI"},
      {label: "Minnesota", value: "MN"},
      {label: "Mississippi", value: "MS"},
      {label: "Missouri", value: "MO"},
      {label: "Montana", value: "MT"},
      {label: "Nebraska", value: "NE"},
      {label: "Nevada", value: "NV"},
      {label: "New Hampshire", value: "NH"},
      {label: "New Jersey", value: "NJ"},
      {label: "New Mexico", value: "NM"},
      {label: "New York", value: "NY"},
      {label: "North Carolina", value: "NC"},
      {label: "North Dakota", value: "ND"},
      {label: "Ohio", value: "OH"},
      {label: "Oklahoma", value: "OK"},
      {label: "Oregon", value: "OR"},
      {label: "Pennsylvania", value: "PA"},
      {label: "Rhode Island", value: "RI"},
      {label: "South Carolina", value: "SC"},
      {label: "South Dakota", value: "SD"},
      {label: "Tennessee", value: "TN"},
      {label: "Texas", value: "TX"},
      {label: "Utah", value: "UT"},
      {label: "Vermont", value: "VT"},
      {label: "Virginia", value: "VA"},
      {label: "Washington", value: "WA"},
      {label: "West Virginia", value: "WV"},
      {label: "Wisconsin", value: "WI"},
      {label: "Wyoming", value: "WY"},
      {label: "AB (Canada)", value: "AB"},
      {label: "BC (Canada)", value: "BC"},
      {label: "MB (Canada)", value: "MB"},
      {label: "NB (Canada)", value: "NB"},
      {label: "NL (Canada)", value: "NL"},
      {label: "NS (Canada)", value: "NS"},
      {label: "NT (Canada)", value: "NT"},
      {label: "NU (Canada)", value: "NU"},
      {label: "ON (Canada)", value: "ON"},
      {label: "PE (Canada)", value: "PE"},
      {label: "QC (Canada)", value: "QC"},
      {label: "SK (Canada)", value: "SK"},
      {label: "YT (Canada)", value: "YT"}
    ]
  }

  countries(): Options<string>[] {
    return [
      {label: "United States", value: "US"},
      {label: "Canada", value: "CA"}
    ]
  }

  //the edit in place component expects a string, so we can't type this as number.
  groupTypes(): Options<string>[] {
    return [
      {label: "Church", value: CustomerGroupType.Church.toString()},
      {label: "Organization", value: CustomerGroupType.Organization.toString()},
      {label: "Foundation", value: CustomerGroupType.Foundation.toString()},
      {label: "Corporation", value: CustomerGroupType.Corporation.toString()},
      {label: "Individual", value: CustomerGroupType.Individual.toString()},
    ]
  }

  potentialGiftStatuses(): Options<PotentialGiftStatus>[] {
    return [
     {label: potentialGiftStatusLabels[PotentialGiftStatus.Identification], value: PotentialGiftStatus.Identification},
     {label: potentialGiftStatusLabels[PotentialGiftStatus.Cultivation], value: PotentialGiftStatus.Cultivation},
     {label: potentialGiftStatusLabels[PotentialGiftStatus.Proposal_Submitted], value: PotentialGiftStatus.Proposal_Submitted},
     {label: potentialGiftStatusLabels[PotentialGiftStatus.Pending], value: PotentialGiftStatus.Pending},
     {label: potentialGiftStatusLabels[PotentialGiftStatus.Committed_Verbal], value: PotentialGiftStatus.Committed_Verbal},
     {label: potentialGiftStatusLabels[PotentialGiftStatus.Committed_Written], value: PotentialGiftStatus.Committed_Written},
     {label: potentialGiftStatusLabels[PotentialGiftStatus.Received], value: PotentialGiftStatus.Received},
     {label: potentialGiftStatusLabels[PotentialGiftStatus.Declined], value: PotentialGiftStatus.Declined},
     {label: potentialGiftStatusLabels[PotentialGiftStatus.Deferred], value: PotentialGiftStatus.Deferred},
    ]
  }

  potentialGiftFrequencies(): Options<PotentialGiftFrequency>[] {
    return [
      {label: potentialGiftFrequencyLabels[PotentialGiftFrequency.Recurring], value: PotentialGiftFrequency.Recurring},
      {label: potentialGiftFrequencyLabels[PotentialGiftFrequency.One_Time], value: PotentialGiftFrequency.One_Time},
    ]
  }

  customerDonationSources(): Options<CustomerDonationSourceType>[] {
    return [
      {label: "Check", value: CustomerDonationSourceType.Check},
      {label: "Paypal", value: CustomerDonationSourceType.Paypal},
      {label: "Stripe (Credit Card)", value: CustomerDonationSourceType.Stripe_CC},
      {label: "Stripe (Bank)", value: CustomerDonationSourceType.Stripe_Bank}
    ]
  }


  disasterIcons(): Options<string>[] {
    return [
      {label: "Default (Handshake)", value: "handshake-angle"},
      {label: "No Icon", value: ""},
      {label: "Earthquake", value: "house-crack"},
      {label: "Landslide", value: "hill-rockslide"},
      {label: "Tsunami", value: "house-tsunami"},
      {label: "Volcanic Activity", value: "volcano"},
      {label: "Avalanche", value: "hill-avalanche"},
      {label: "Flood", value: "house-flood-water"},
      {label: "Food Insecurity", value: "wheat-awn-circle-exclamation"},
      {label: "Extreme Temperatures", value: "temperature-full"},
      {label: "Drought", value: "sun-plant-wilt"},
      {label: "Wildfire", value: "fire"},
      {label: "Tornado", value: "tornado"},
      {label: "Storm", value: "cloud-showers-water"},
      {label: "Hurricane", value: "hurricane"},
      {label: "Disease/Epidemic", value: "vial-virus"},
      {label: "Conflict", value: "tower-broadcast"},
      {label: "Displaced Populations", value: "tents"},
      {label: "Feeding Program", value: "bowl-food"}
    ]
  }

  disasterRatings(): Options<number>[] {
    return [
     {label: "Mild", value: 1},
     {label: "High", value: 2},
     {label: "Severe", value: 3}
    ]
  }

  profileNeedTypes(): Options<string>[] {
    return [ 
      { label: '-', value: '' },
      { label: 'Construction', value: 'Construction' },
      { label: 'Service', value: 'Service' },
      { label: 'Prayer', value: 'Prayer' },
      { label: 'Spiritual', value: 'Spiritual' },
      { label: 'Fundraising', value: 'Fundraising' },
      { label: 'Event', value: 'Event' },
      { label: 'Ministry', value: 'Ministry' },
      { label: 'Food Distribution', value: 'Food Distribution' },
      { label: 'Medical', value: 'Medical' },
      { label: 'Other', value: 'Other' }
    ];
  }

  profileEventTypes(): Options<string>[] {
    return [
      { label: '-', value: '' },
      { label: 'Education', value: 'Education' },
      { label: 'Children', value: 'Children' },
      { label: 'Youth', value: 'Youth' },
      { label: 'Young Adult', value: 'Young Adult' },
      { label: 'Adult', value: 'Adult' },
      { label: 'Men', value: 'Men' },
      { label: 'Women', value: 'Women' },
      { label: 'Meeting', value: 'Meeting' },
      { label: 'Mercy', value: 'Mercy' },
      { label: 'Service (Worship)', value: 'Service (Worship)' },
      { label: 'Bible Study', value: 'Bible Study' },
      { label: 'Service (Prayer)', value: 'Service (Prayer)' },
      { label: 'Social Gathering', value: 'Social Gathering' },
      { label: 'Evangelism', value: 'Evangelism' },
      { label: 'Community', value: 'Community' },
      { label: 'Visiting', value: 'Visiting' },
      { label: 'Other', value: 'Other' }
    ];
  }


}
